import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import * as Sentry from "@sentry/react";
import { observer } from "mobx-react";
import { RootStoreContext } from "src/stores/RootStore";
import { ITicketImage } from "src/stores/TicketStore";
import { IFile } from "src/types";
import { NetworkConfig } from "src/network/NetworkConfig";
import { UI } from "@wwimmo/ui";
import styles from "./ImageCheckboxGrid.module.css";
import { useTranslation } from "react-i18next";

const ImageCheckboxGridBase = () => {
    const { t } = useTranslation();
    const { ticketStore, assignOrderModalStore } = useContext(RootStoreContext);
    const [selectedImages, setSelectedImages] = useState<ITicketImage[]>([]);
    const [selectedIncidentFiles, setSelectedIncidentFiles] = useState<IFile[]>([]);

    const images = useMemo(() => {
        return ticketStore.currentTicket?.damageNotificationDetails?.images &&
            ticketStore.currentTicket.damageNotificationDetails.images.length > 0
            ? ticketStore.currentTicket.damageNotificationDetails.images
            : ticketStore.currentTicket?.keyOrderNotificationDetails?.images &&
                ticketStore.currentTicket.keyOrderNotificationDetails.images.length > 0
              ? ticketStore.currentTicket.keyOrderNotificationDetails.images
              : ticketStore.currentTicket?.questionToDocumentNotificationDetails?.images &&
                  ticketStore.currentTicket.questionToDocumentNotificationDetails.images.length > 0
                ? ticketStore.currentTicket.questionToDocumentNotificationDetails.images
                : [];
    }, [ticketStore.currentTicket]);

    const incidentFiles = useMemo(() => {
        return ticketStore.currentTicket?.incidentFiles && ticketStore.currentTicket.incidentFiles.length > 0
            ? ticketStore.currentTicket.incidentFiles
            : [];
    }, [ticketStore.currentTicket]);

    const labelImage = useMemo(() => {
        const serialNumberImage = ticketStore.currentTicket?.damageNotificationDetails?.serialNumberImage;
        return serialNumberImage?.fileId ? serialNumberImage : undefined;
    }, [ticketStore.currentTicket]);

    useEffect(() => {
        if (images.length > 0) {
            const initialSelection = images.slice(0, 2); // Get the first two images
            setSelectedImages(initialSelection);

            // Extract and send fileIds of the initially selected images
            const initialFileIds = initialSelection
                .map((img) => img.fileId)
                .filter((fileId): fileId is string => fileId !== undefined);

            assignOrderModalStore.setSelectedImageIds(initialFileIds);
        }
    }, [images, assignOrderModalStore]);

    const handleCheckboxChange = useCallback(
        (image: ITicketImage) => async (e: any) => {
            const updatedSelection = selectedImages.includes(image)
                ? selectedImages.filter((img) => img.fileId !== image.fileId)
                : [...selectedImages, image];

            setSelectedImages(updatedSelection);

            const selectedFileIds = updatedSelection
                .map((img) => img.fileId)
                .filter((fileId): fileId is string => fileId !== undefined);

            assignOrderModalStore.setSelectedImageIds(selectedFileIds);
        },
        [selectedImages, assignOrderModalStore]
    );

    const handleIncidentFileCheckboxChange = useCallback(
        (file: IFile) => async (e: any) => {
            const updatedSelection = selectedIncidentFiles.includes(file)
                ? selectedIncidentFiles.filter((f) => f.fileId !== file.fileId)
                : [...selectedIncidentFiles, file];

            setSelectedIncidentFiles(updatedSelection);

            const selectedFileIds = updatedSelection
                .map((f) => f.fileId)
                .filter((fileId): fileId is string => fileId !== undefined);

            assignOrderModalStore.setInvoiceAttachments(selectedFileIds);
        },
        [selectedIncidentFiles, assignOrderModalStore]
    );

    if (!labelImage && images.length === 0 && incidentFiles.length === 0) {
        return null;
    }

    return (
        <div className={styles.container}>
            <p className={styles.hint}>{t("screens.tickets.modals.assign_order.attachments_hint")}</p>
            <div className={styles.gridContainer}>
                {labelImage ? (
                    <div
                        className={`${styles.imageWrapper} ${styles.lockedWrapper} selected`}
                        title={t("screens.tickets.modals.assign_order.label_image_always_sent").toString()}
                    >
                        <img
                            src={labelImage.url || `data:${labelImage.mimetype};base64,${labelImage.base64String}`}
                            alt={labelImage.name}
                            className={styles.styledCheckedImage}
                        />
                        <div className={styles.checkboxWrapper}>
                            <input
                                type="checkbox"
                                checked={true}
                                disabled={true}
                                readOnly={true}
                                className={`${styles.checkbox} ${styles.checkboxChecked}`}
                            />
                        </div>
                        <span className={styles.lockedCaption}>
                            {t("screens.tickets.modals.assign_order.label_image_always_sent")}
                        </span>
                    </div>
                ) : undefined}
                {images.map((image, index) => {
                    const imageSrc = image.url || `data:${image.mimetype};base64,${image.base64String}`;

                    const isChecked = selectedImages.includes(image);
                    const checkboxClass = isChecked ? `${styles.checkbox} ${styles.checkboxChecked}` : styles.checkbox;
                    const imageWrapperClass = isChecked ? `${styles.imageWrapper} selected` : styles.imageWrapper;

                    return (
                        <div key={`img-${index}`} className={imageWrapperClass}>
                            <img
                                src={imageSrc}
                                alt={image.name}
                                className={isChecked ? styles.styledCheckedImage : styles.styledImage}
                                onClick={handleCheckboxChange(image)}
                            />
                            <div className={styles.checkboxWrapper}>
                                <input
                                    type="checkbox"
                                    checked={isChecked}
                                    onChange={handleCheckboxChange(image)}
                                    className={checkboxClass}
                                />
                            </div>
                        </div>
                    );
                })}
                {incidentFiles.map((file, index) => {
                    const isImage = file.mimetype?.startsWith("image");
                    const isPdf = file.mimetype === "application/pdf";

                    const imageSrc = file.url
                        ? file.url
                        : file.base64String
                          ? `data:${file.mimetype};base64,${file.base64String}`
                          : file.fileId
                            ? isImage
                                ? NetworkConfig.openThumbnailUrl + file.fileId
                                : NetworkConfig.datafileUrl + file.fileId
                            : undefined;

                    const isChecked = selectedIncidentFiles.includes(file);
                    const checkboxClass = isChecked ? `${styles.checkbox} ${styles.checkboxChecked}` : styles.checkbox;
                    const imageWrapperClass = isChecked ? `${styles.imageWrapper} selected` : styles.imageWrapper;

                    return (
                        <div key={`file-${index}`} className={imageWrapperClass}>
                            {isImage ? (
                                <img
                                    src={imageSrc}
                                    alt={file.name}
                                    className={isChecked ? styles.styledCheckedImage : styles.styledImage}
                                    onClick={handleIncidentFileCheckboxChange(file)}
                                />
                            ) : isPdf ? (
                                <div
                                    className={isChecked ? styles.styledCheckedImage : styles.styledImage}
                                    onClick={handleIncidentFileCheckboxChange(file)}
                                    style={{
                                        display: "flex",
                                        flexDirection: "column",
                                        justifyContent: "center",
                                        alignItems: "center",
                                        backgroundColor: "#f0f0f0",
                                        position: "relative"
                                    }}
                                >
                                    <UI.Icon
                                        color="var(--grey)"
                                        icon={UI.SVGIcon.Pdf}
                                        size={40}
                                        style={{ marginBottom: "15px" }}
                                    />
                                    <span
                                        style={{
                                            position: "absolute",
                                            bottom: "5px",
                                            left: "5px",
                                            right: "5px",
                                            fontSize: "10px",
                                            whiteSpace: "nowrap",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            textAlign: "center",
                                            color: "#333",
                                            fontWeight: "normal"
                                        }}
                                    >
                                        {file.name}
                                    </span>
                                </div>
                            ) : (
                                <div
                                    className={isChecked ? styles.styledCheckedImage : styles.styledImage}
                                    onClick={handleIncidentFileCheckboxChange(file)}
                                    style={{
                                        display: "flex",
                                        justifyContent: "center",
                                        alignItems: "center",
                                        backgroundColor: "#f0f0f0",
                                        color: "#333",
                                        fontSize: "12px",
                                        textAlign: "center",
                                        padding: "5px",
                                        overflow: "hidden"
                                    }}
                                >
                                    {file.name || "File"}
                                </div>
                            )}
                            <div className={styles.checkboxWrapper}>
                                <input
                                    type="checkbox"
                                    checked={isChecked}
                                    onChange={handleIncidentFileCheckboxChange(file)}
                                    className={checkboxClass}
                                />
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export const ImageCheckboxGrid = Sentry.withProfiler(observer(ImageCheckboxGridBase));
